import React, { useState, useRef, useEffect, useCallback } from 'react';

const MobilePlayer = ({ song, isPlaying = false, onClose, onPlayPause, onNext, onPrevious, onRepeat, onForward10 }) => {
  const [isImmersiveMode, setIsImmersiveMode] = useState(false);
  const [currentTime, setCurrentTime] = useState(0); // in seconds
  const [totalTime, setTotalTime] = useState(192); // 3:12 in seconds
  const [progress, setProgress] = useState(0);
  const [localIsPlaying, setLocalIsPlaying] = useState(isPlaying);
  const videoRef = useRef(null);
  
  // Mock section data - positions as percentages (0-1) with titles
  const sections = [
    { position: 0.15, title: "Intro" },
    { position: 0.32, title: "Verse 1" },
    { position: 0.58, title: "Chorus" },
    { position: 0.75, title: "Verse 2" },
    { position: 0.89, title: "Outro" }
  ];
  const [isDragging, setIsDragging] = useState(false);
  const [tempProgress, setTempProgress] = useState(progress);
  const [isExpanded, setIsExpanded] = useState(false);
  const [scrubberPosition, setScrubberPosition] = useState(0);
  const [currentLyricIndex, setCurrentLyricIndex] = useState(0);
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const progressBarRef = useRef(null);

  // Fake lyrics data - pairs of lines
  const lyricsLines = [
    ["Dancing through the starlight", "Colors bleeding into night"],
    ["Electric dreams are calling", "From a world beyond our sight"],
    ["Gravity can't hold us", "When the music sets us free"],
    ["Floating through dimensions", "Where we're meant to be"],
    ["Synthesizers whisper", "Secrets from another time"],
    ["Every beat's a heartbeat", "Every note's a paradigm"]
  ];

  // Format time from seconds to MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate current time based on progress (or temp progress when dragging)
  const getCurrentTime = () => {
    const currentProgress = isDragging ? tempProgress : progress;
    return Math.floor(currentProgress * totalTime);
  };

  // Get section title if we're within 4 seconds before or after a section
  const getCurrentSectionTitle = () => {
    const currentProgress = isDragging ? tempProgress : progress;
    const currentTimeSeconds = currentProgress * totalTime;
    
    for (const section of sections) {
      const sectionTimeSeconds = section.position * totalTime;
      const timeDifference = Math.abs(currentTimeSeconds - sectionTimeSeconds);
      
      // Show section title if within 4 seconds before or after
      if (timeDifference <= 4) {
        return section.title;
      }
    }
    return null;
  };

  // Handle seeking to specific time
  const seekToTime = (newProgress) => {
    setProgress(newProgress);
    setCurrentTime(Math.floor(newProgress * totalTime));
  };

  // Handle forward 10 seconds
  const handleForward10Seconds = () => {
    const newTime = Math.min(getCurrentTime() + 10, totalTime);
    const newProgress = newTime / totalTime;
    seekToTime(newProgress);
    onForward10?.();
  };

  // Handle backward 10 seconds
  const handleBackward10Seconds = () => {
    const newTime = Math.max(getCurrentTime() - 10, 0);
    const newProgress = newTime / totalTime;
    seekToTime(newProgress);
  };

  // Handle back to beginning
  const handleBackToBeginning = () => {
    seekToTime(0);
    onRepeat?.();
  };

  const handlePlayPause = () => {
    setLocalIsPlaying(!localIsPlaying);
    onPlayPause?.();
  };

  // Update progress in real-time when playing
  useEffect(() => {
    let intervalId;
    
    if (localIsPlaying && !isDragging) {
      intervalId = setInterval(() => {
        setProgress(prevProgress => {
          const newProgress = prevProgress + (0.1 / totalTime); // Advance by 0.1 second
          if (newProgress >= 1) {
            setLocalIsPlaying(false); // Stop when song ends
            return 1;
          }
          return newProgress;
        });
        
        setCurrentTime(prevTime => {
          const newTime = prevTime + 0.1;
          if (newTime >= totalTime) {
            return totalTime;
          }
          return newTime;
        });
      }, 100); // Update every 100ms (10 times per second)
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [localIsPlaying, isDragging, totalTime]);

  // Animate lyrics every 4 seconds when playing
  useEffect(() => {
    let lyricIntervalId;
    
    if (localIsPlaying && !isDragging) {
      lyricIntervalId = setInterval(() => {
        setCurrentLyricIndex(prevIndex => (prevIndex + 1) % lyricsLines.length);
        setCurrentLineIndex(0); // Reset line index when changing lyric pair
      }, 4000); // Change lyrics every 4 seconds
    }
    
    return () => {
      if (lyricIntervalId) {
        clearInterval(lyricIntervalId);
      }
    };
  }, [localIsPlaying, isDragging, lyricsLines.length]);

  // Animate line highlighting every 2 seconds when playing
  useEffect(() => {
    let lineIntervalId;
    
    if (localIsPlaying && !isDragging) {
      lineIntervalId = setInterval(() => {
        setCurrentLineIndex(prevLineIndex => {
          const currentLyrics = lyricsLines[currentLyricIndex];
          const nextLineIndex = prevLineIndex + 1;
          
          // If we've finished both lines, wait for the next lyric pair
          if (nextLineIndex >= currentLyrics.length) {
            return 0; // Reset to line 0 and wait for next lyric pair
          }
          
          return nextLineIndex;
        });
      }, 2000); // Highlight new line every 2 seconds
    }
    
    return () => {
      if (lineIntervalId) {
        clearInterval(lineIntervalId);
      }
    };
  }, [localIsPlaying, isDragging, currentLyricIndex, lyricsLines]);

  // Sync video playback with song state
  useEffect(() => {
    if (videoRef.current) {
      if (localIsPlaying) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  }, [localIsPlaying]);

  const handleScreenTap = () => {
    setIsImmersiveMode(!isImmersiveMode);
  };

  // Render line with line-by-line highlighting
  const renderHighlightedLine = (lineText, lineIdx) => {
    const isCurrentLine = lineIdx === currentLineIndex;
    const isHighlighted = isCurrentLine;
    
    // Debug logging
    console.log('Line highlight state:', {
      currentLineIndex,
      lineIdx,
      isHighlighted,
      lineText
    });
    
    return (
      <p 
        key={lineIdx}
        className="text-xl font-bold text-left leading-tight"
        style={{
          color: '#ffffff',
          opacity: isHighlighted ? 1 : 0.4,
          transition: 'opacity 300ms ease-in-out'
        }}
      >
        {lineText}
      </p>
    );
  };

  const handleProgressStart = (e) => {
    e.preventDefault();
    setIsDragging(true);
    updateProgress(e);
  };

  const updateProgress = useCallback((e) => {
    if (progressBarRef.current) {
      const rect = progressBarRef.current.getBoundingClientRect();
      const clientX = e.clientX || (e.touches && e.touches[0]?.clientX) || 0;
      const newProgress = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      setTempProgress(newProgress);
      
      // Calculate pill position with boundary constraints
      const pillWidth = 60; // Approximate width of the time pill
      const minPosition = pillWidth / 2;
      const maxPosition = rect.width - (pillWidth / 2);
      const constrainedPosition = Math.max(minPosition, Math.min(maxPosition, clientX - rect.left));
      setScrubberPosition(constrainedPosition);
    }
  }, []);

  const handleProgressEnd = useCallback(() => {
    setIsDragging(false);
    seekToTime(tempProgress);
  }, [tempProgress]);

  const handleProgressMove = useCallback((e) => {
    if (isDragging) {
      try {
        e.preventDefault();
        updateProgress(e);
      } catch (error) {
        console.warn('Touch event preventDefault failed:', error);
        updateProgress(e);
      }
    }
  }, [isDragging, updateProgress]);

  // Add global event listeners for both mouse and touch
  useEffect(() => {
    if (isDragging) {
      // Mouse events
      document.addEventListener('mousemove', handleProgressMove, { passive: false });
      document.addEventListener('mouseup', handleProgressEnd);
      
      // Touch events
      document.addEventListener('touchmove', handleProgressMove, { passive: false });
      document.addEventListener('touchend', handleProgressEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleProgressMove);
        document.removeEventListener('mouseup', handleProgressEnd);
        document.removeEventListener('touchmove', handleProgressMove);
        document.removeEventListener('touchend', handleProgressEnd);
      };
    }
  }, [isDragging, handleProgressMove, handleProgressEnd]);

  const displayProgress = isDragging ? tempProgress : progress;

  return (
    <div className="fixed inset-0 z-[100] bg-black">
      {/* Full black background to cover everything */}
      <div className="absolute inset-0 bg-black" />
      
      {/* Mobile Player Container - Centered */}
      <div className="flex items-center justify-center w-full h-full">
        <div 
          className="w-[402px] h-[874px] relative overflow-hidden rounded-[48px] bg-black"
        >
        <div className="w-[402px] h-[874px]">
          {/* Check if it's a video file */}
          <video
            ref={videoRef}
            className="w-[402px] h-[874px] absolute left-[-0.5px] top-[-0.5px] rounded-[26px] object-cover object-center"
            src="/background.mov"
            loop
            muted
            playsInline
          />
          <div
            className="w-[402px] h-[874px] absolute left-[-0.5px] top-[-0.5px]"
            style={{
              background:
                "linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.4) 39.63%, rgba(0,0,0,0) 64.48%, rgba(0,0,0,0) 84.24%, rgba(0,0,0,0.1) 99.2%)",
            }}
          />
        </div>

        <div 
          className="absolute top-[200px] left-[50px] right-[50px] bottom-[300px] z-0"
          onClick={handleScreenTap}
        />

        {/* Close Button - Top Left */}
        <div 
          className={`flex flex-col justify-center items-center w-11 h-11 absolute left-3 top-[65px] gap-[5px] p-2.5 rounded-[56px] bg-[#696969]/20 border-[0.5px] border-white/10 backdrop-blur-[94px] cursor-pointer hover:scale-105 ${
            isImmersiveMode ? 'opacity-0 pointer-events-none' : 'opacity-100'
          }`}
          style={{
            transition: 'opacity 200ms cubic-bezier(0.34, 1.56, 0.64, 1), transform 200ms cubic-bezier(0.34, 1.56, 0.64, 1)'
          }}
          onClick={onClose}
        >
          <svg
            width={20}
            height={20}
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="flex-grow-0 flex-shrink-0 w-5 h-5"
          >
            <path
              d="M9.99423 12.292L15.2755 7.01076C15.4551 6.83112 15.6677 6.7443 15.9132 6.75029C16.1587 6.75628 16.3712 6.84909 16.5509 7.02872C16.7305 7.20836 16.8203 7.42092 16.8203 7.66642C16.8203 7.91192 16.7305 8.12449 16.5509 8.30412L11.0181 13.8189C10.8744 13.9626 10.7128 14.0704 10.5331 14.1422C10.3535 14.2141 10.1739 14.25 9.99423 14.25C9.81459 14.25 9.63496 14.2141 9.45533 14.1422C9.27569 14.0704 9.11402 13.9626 8.97031 13.8189L3.43759 8.28616C3.25796 8.10652 3.17114 7.89695 3.17712 7.65744C3.18311 7.41793 3.27592 7.20836 3.45556 7.02872C3.63519 6.84909 3.84776 6.75927 4.09326 6.75927C4.33876 6.75927 4.55132 6.84909 4.73096 7.02872L9.99423 12.292Z"
              fill="#FAF7F5"
            />
          </svg>
        </div>

        {/* Main Content Container */}
        <div className={`flex flex-col justify-end items-start w-full absolute left-0 px-3 ${
          isImmersiveMode ? 'bottom-[5px]' : 'bottom-[100px]'
        }`}
        style={{
          transition: 'bottom 500ms cubic-bezier(0.34, 1.56, 0.64, 1)'
        }}>
          <div className="flex flex-col justify-end items-start self-stretch flex-grow-0 flex-shrink-0 gap-3">
            
            {/* Song Info and Content */}
            <div className={`flex flex-col justify-end items-start flex-grow-0 flex-shrink-0 w-full gap-3 transition-opacity duration-200 ${
              isDragging ? 'opacity-50' : 'opacity-100'
            }`}>
              {/* Song Details with Actions */}
              <div className="flex justify-start items-end flex-grow-0 flex-shrink-0 w-full gap-3">
                <div className="flex flex-col justify-start items-start flex-grow gap-2">
                  <div className="flex flex-col justify-start items-start self-stretch flex-grow-0 flex-shrink-0 gap-1">
                    <div className="flex justify-start items-end self-stretch flex-grow-0 flex-shrink-0 relative gap-2.5">
                      <p className="flex-grow flex-shrink-0 text-xl font-medium text-left text-[#faf7f5]">
                        {song?.title || 'Magical Vibes'}
                      </p>
                    </div>
                    <div className="flex justify-start items-center flex-grow-0 flex-shrink-0 relative gap-2 rounded-xl">
                      <svg
                        width={18}
                        height={18}
                        viewBox="0 0 18 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="flex-grow-0 flex-shrink-0"
                        preserveAspectRatio="none"
                      >
                        <circle cx={9} cy={9} r={8} fill="url(#paint0_linear_1584_4168)" />
                        <circle cx={9} cy={9} r="8.25" stroke="white" strokeOpacity="0.2" strokeWidth="0.5" />
                        <defs>
                          <linearGradient
                            id="paint0_linear_1584_4168"
                            x1={9}
                            y1={1}
                            x2={9}
                            y2={17}
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop stopColor="#2A2A2A" />
                            <stop offset={1} />
                          </linearGradient>
                        </defs>
                      </svg>
                      <p className="flex-grow-0 flex-shrink-0 text-sm font-medium text-left text-[#faf7f5]">
                        {song?.artist || 'kaveh'}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col justify-start items-start flex-grow flex-shrink-0 w-full relative gap-0.5">
                    {isExpanded ? (
                      <>
                        <p className="flex-1 text-sm text-left text-white leading-relaxed break-words whitespace-normal mr-3">
                          This song is about wanting to escape to another planet where the colors are more vibrant and the sounds are more psychedelic. It's an electronic indie track that captures the feeling of wanderlust and the desire for otherworldly experiences. #psychedelic #electronic #indie #space #escape #otherworldly
                        </p>
                        <div className="w-full flex justify-end" style={{ paddingRight: '16px' }}>
                          <span 
                            className="text-sm text-white font-bold cursor-pointer"
                            onClick={() => setIsExpanded(!isExpanded)}
                          >
                            ...less
                          </span>
                        </div>
                      </>
                    ) : (
                      <p className="flex-1 text-sm text-left text-white leading-relaxed break-words whitespace-normal mr-3">
                        This song is about wanting to escape to another planet #psychedelic #electronic #indie...
                        <span 
                          className="text-white font-bold cursor-pointer ml-1"
                          onClick={() => setIsExpanded(!isExpanded)}
                        >
                          more
                        </span>
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col justify-center items-end flex-grow-0 flex-shrink-0 w-11 gap-2.5">
                  <div className="flex flex-col justify-center items-center self-stretch flex-grow-0 flex-shrink-0 relative gap-1">
                    <div className="flex flex-col justify-center items-center self-stretch flex-grow-0 flex-shrink-0 h-11 relative gap-[5px] p-2.5 rounded-[56px] bg-[#696969]/20 border-[0.5px] border-white/10 backdrop-blur-[94px]">
                      <svg
                        width={24}
                        height={24}
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="flex-grow-0 flex-shrink-0 w-6 h-6"
                        preserveAspectRatio="none"
                      >
                        <path
                          d="M21.6935 5.44345C21.1735 5.27345 20.6035 5.55345 20.4235 6.07345L20.2735 6.53345C18.6335 3.96345 15.8835 2.25345 12.7435 2.02345C8.22351 1.71345 4.10351 4.50345 2.74351 8.82345C2.57351 9.35345 2.87351 9.91345 3.39351 10.0735C3.49351 10.1034 3.59351 10.1235 3.69351 10.1235C4.11351 10.1235 4.51351 9.85345 4.64352 9.42345C5.72352 5.98345 8.99352 3.77345 12.5935 4.02345C15.0635 4.19345 17.2335 5.53345 18.5435 7.54345L17.9035 7.33345C17.3835 7.16345 16.8135 7.44345 16.6335 7.96345C16.4535 8.48345 16.7435 9.05345 17.2635 9.23345L20.0935 10.1835C20.1935 10.2135 20.3035 10.2334 20.4135 10.2334C20.5635 10.2334 20.7235 10.1935 20.8635 10.1335C21.1035 10.0135 21.2835 9.80345 21.3635 9.55345L22.3135 6.72345C22.4935 6.20345 22.2035 5.63345 21.6835 5.45345L21.6935 5.44345Z"
                          fill="#FAF7F5"
                        />
                        <path
                          d="M20.4835 14.7721C19.9535 14.6121 19.3935 14.9021 19.2335 15.4221C18.1535 18.8621 14.8835 21.0721 11.2835 20.8221C8.81352 20.6521 6.64352 19.3121 5.33352 17.3021L5.97352 17.5121C6.50352 17.6921 7.06352 17.4021 7.24352 16.8821C7.42352 16.3621 7.13352 15.7921 6.61352 15.6121L3.78352 14.6621C3.53352 14.5821 3.25352 14.6021 3.02352 14.7121C2.78352 14.8321 2.60352 15.0421 2.52352 15.2921L1.55352 18.1421C1.37352 18.6621 1.66352 19.2321 2.18352 19.4121C2.29352 19.4521 2.39352 19.4621 2.50352 19.4621C2.92352 19.4621 3.31352 19.2021 3.45352 18.7821L3.60352 18.3221C5.24352 20.9021 7.99352 22.6121 11.1335 22.8321C11.3735 22.8521 11.6035 22.8621 11.8335 22.8621C16.0735 22.8621 19.8435 20.1321 21.1235 16.0421C21.2935 15.5121 20.9935 14.9521 20.4735 14.7921L20.4835 14.7721Z"
                          fill="#FAF7F5"
                        />
                        <path
                          d="M12.1935 9.4134C12.1035 9.1734 11.7635 9.1734 11.6735 9.4134L10.9735 11.3034C10.9535 11.3734 10.8935 11.4334 10.8135 11.4634L8.92353 12.1634C8.68353 12.2534 8.68353 12.5934 8.92353 12.6834L10.8135 13.3834C10.8835 13.4034 10.9435 13.4634 10.9735 13.5434L11.6735 15.4334C11.7635 15.6734 12.1035 15.6734 12.1935 15.4334L12.8935 13.5434C12.9135 13.4734 12.9735 13.4134 13.0535 13.3834L14.9435 12.6834C15.1835 12.5934 15.1835 12.2534 14.9435 12.1634L13.0535 11.4634C12.9835 11.4434 12.9235 11.3834 12.8935 11.3034L12.1935 9.4134Z"
                          fill="#FAF7F5"
                        />
                      </svg>
                    </div>
                    <p className="flex-grow-0 flex-shrink-0 text-[10px] text-center text-white" style={{ fontFamily: 'Input Sans, monospace' }}>
                      REMIX
                    </p>
                  </div>
                  <div className="flex flex-col justify-center items-center self-stretch flex-grow-0 flex-shrink-0 relative gap-1">
                    <div className="flex flex-col justify-center items-center self-stretch flex-grow-0 flex-shrink-0 h-11 relative gap-[5px] p-2.5 rounded-[56px] bg-[#696969]/20 border-[0.5px] border-white/10 backdrop-blur-[94px]">
                      <svg
                        width={24}
                        height={24}
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="flex-grow-0 flex-shrink-0 w-6 h-6"
                        preserveAspectRatio="none"
                      >
                        <path
                          d="M18.881 8.28791C19.3127 8.28791 19.6905 8.45271 20.0143 8.78231C20.3381 9.11191 20.5 9.49645 20.5 9.93592V11.5839C20.5 11.6801 20.4899 11.7831 20.4696 11.8929C20.4494 12.0028 20.419 12.1058 20.3786 12.2019L17.95 18.0112C17.8286 18.2859 17.6262 18.5193 17.3429 18.7116C17.0595 18.9039 16.7627 19 16.4524 19H9.97619C9.53095 19 9.1498 18.8386 8.83274 18.5159C8.51567 18.1932 8.35714 17.8052 8.35714 17.352L8.35714 8.96771C8.35714 8.74798 8.40099 8.53854 8.48869 8.33941C8.57639 8.14027 8.69444 7.96517 8.84286 7.8141L13.2345 3.36446C13.4369 3.1722 13.6764 3.05546 13.953 3.01426C14.2296 2.97306 14.496 3.02113 14.7524 3.15846C15.0087 3.2958 15.1942 3.48807 15.3089 3.73527C15.4236 3.98247 15.4472 4.23654 15.3798 4.49747L14.469 8.28791L18.881 8.28791ZM5.11905 19C4.67381 19 4.29266 18.8386 3.9756 18.5159C3.65853 18.1932 3.5 17.8052 3.5 17.352L3.5 9.93592C3.5 9.48272 3.65853 9.09475 3.9756 8.77201C4.29266 8.44928 4.67381 8.28791 5.11905 8.28791C5.56429 8.28791 5.94544 8.44928 6.2625 8.77201C6.57956 9.09475 6.7381 9.48272 6.7381 9.93592V17.352C6.7381 17.8052 6.57956 18.1932 6.2625 18.5159C5.94544 18.8386 5.56429 19 5.11905 19Z"
                          fill="#FAF7F5"
                        />
                      </svg>
                    </div>
                    <p className="flex-grow-0 flex-shrink-0 text-[10px] text-center text-white" style={{ fontFamily: 'Input Sans, monospace' }}>
                      124
                    </p>
                  </div>
                  <div className="flex flex-col justify-center items-center self-stretch flex-grow-0 flex-shrink-0 relative gap-1">
                    <div className="flex flex-col justify-center items-center self-stretch flex-grow-0 flex-shrink-0 h-11 relative gap-[5px] p-2.5 rounded-[56px] bg-[#696969]/20 border-[0.5px] border-white/10 backdrop-blur-[94px]">
                      <svg
                        width={24}
                        height={24}
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="flex-grow-0 flex-shrink-0 w-6 h-6"
                        preserveAspectRatio="none"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M21.1284 11.3125C21.1284 15.3511 17.099 18.625 12.1284 18.625C11.2053 18.625 10.3146 18.5121 9.47614 18.3023C9.22723 18.24 8.96327 18.2636 8.73532 18.3814L4.26445 20.6919C3.36888 21.1547 2.42875 20.1516 2.94853 19.2879L4.64211 16.4735C4.87539 16.0859 4.81328 15.5942 4.53398 15.2383C3.64405 14.1041 3.12839 12.7572 3.12839 11.3125C3.12839 7.27392 7.15783 4 12.1284 4C17.099 4 21.1284 7.27392 21.1284 11.3125ZM9.12839 12.3C9.68067 12.3 10.1284 11.8523 10.1284 11.3C10.1284 10.7477 9.68067 10.3 9.12839 10.3C8.5761 10.3 8.12839 10.7477 8.12839 11.3C8.12839 11.8523 8.5761 12.3 9.12839 12.3ZM12.1284 12.3C12.6807 12.3 13.1284 11.8523 13.1284 11.3C13.1284 10.7477 12.6807 10.3 12.1284 10.3C11.5761 10.3 11.1284 10.7477 11.1284 11.3C11.1284 11.8523 11.5761 12.3 12.1284 12.3ZM16.1284 11.3C16.1284 11.8523 15.6807 12.3 15.1284 12.3C14.5761 12.3 14.1284 11.8523 14.1284 11.3C14.1284 10.7477 14.5761 10.3 15.1284 10.3C15.6807 10.3 16.1284 10.7477 16.1284 11.3Z"
                          fill="#FAF7F5"
                        />
                      </svg>
                    </div>
                    <p className="flex-grow-0 flex-shrink-0 text-[10px] text-center text-white" style={{ fontFamily: 'Input Sans, monospace' }}>
                      293
                    </p>
                  </div>
                  <div className="flex flex-col justify-center items-center self-stretch flex-grow-0 flex-shrink-0 relative gap-1">
                    <div className="flex flex-col justify-center items-center self-stretch flex-grow-0 flex-shrink-0 h-11 relative gap-[5px] p-2.5 rounded-[56px] bg-[#696969]/20 border-[0.5px] border-white/10 backdrop-blur-[94px]">
                      <svg
                        width={24}
                        height={24}
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="flex-grow-0 flex-shrink-0 w-6 h-6 relative"
                        preserveAspectRatio="xMidYMid meet"
                      >
                        <path
                          d="M13.0003 4.04028C13.0003 3.0888 14.1737 2.63853 14.8102 3.34576L21.7189 11.0222C22.0743 11.417 22.0743 12.0164 21.7189 12.4112L14.8102 20.0876C14.1737 20.7948 13.0003 20.3446 13.0003 19.3931V15.8694C9.23897 15.8696 6.53658 17.5028 4.27151 19.223C3.60064 19.7325 2.6445 19.2965 2.70273 18.4562C3.218 11.0204 9.17672 7.5005 13.0003 7.5V4.04028Z"
                          fill="#FAF7F5"
                        />
                      </svg>
                    </div>
                    <p className="flex-grow-0 flex-shrink-0 text-[10px] text-center text-white" style={{ fontFamily: 'Input Sans, monospace' }}>
                      SHARE
                    </p>
                  </div>
                  <div className="flex flex-col justify-center items-center self-stretch flex-grow-0 flex-shrink-0 h-11 relative gap-[5px] p-2.5 rounded-[56px] bg-[#696969]/20 border-[0.5px] border-white/10 backdrop-blur-[94px]">
                    <svg
                      width={24}
                      height={24}
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="flex-grow-0 flex-shrink-0 w-6 h-6"
                      preserveAspectRatio="none"
                    >
                      <path
                        d="M6 14C5.45 14 4.97917 13.8042 4.5875 13.4125C4.19583 13.0208 4 12.55 4 12C4 11.45 4.19583 10.9792 4.5875 10.5875C4.97917 10.1958 5.45 10 6 10C6.55 10 7.02083 10.1958 7.4125 10.5875C7.80417 10.9792 8 11.45 8 12C8 12.55 7.80417 13.0208 7.4125 13.4125C7.02083 13.8042 6.55 14 6 14ZM12 14C11.45 14 10.9792 13.8042 10.5875 13.4125C10.1958 13.0208 10 12.55 10 12C10 11.45 10.1958 10.9792 10.5875 10.5875C10.9792 10.1958 11.45 10 12 10C12.55 10 13.0208 10.1958 13.4125 10.5875C13.8042 10.9792 14 11.45 14 12C14 12.55 13.8042 13.0208 13.4125 13.4125C13.0208 13.8042 12.55 14 12 14ZM18 14C17.45 14 16.9792 13.8042 16.5875 13.4125C16.1958 13.0208 16 12.55 16 12C16 11.45 16.1958 10.9792 16.5875 10.5875C16.9792 10.1958 17.45 10 18 10C18.55 10 19.0208 10.1958 19.4125 10.5875C19.8042 10.9792 20 11.45 20 12C20 12.55 19.8042 13.0208 19.4125 13.4125C19.0208 13.8042 18.55 14 18 14Z"
                        fill="#FAF7F5"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Interactive Progress Bar */}
            <div className="flex flex-col justify-center items-center flex-grow-0 flex-shrink-0 h-[35px] w-full gap-3 pt-[3px]">
              <div 
                ref={progressBarRef}
                className="flex justify-start items-center self-stretch flex-grow-0 flex-shrink-0 h-2.5 relative cursor-pointer"
                onMouseDown={handleProgressStart}
                onTouchStart={handleProgressStart}
              >
                {/* Time Pill - appears only when dragging */}
                {isDragging && (
                  <div 
                    className="absolute z-10 px-2 py-1 bg-black rounded-full"
                    style={{
                      left: `${scrubberPosition}px`,
                      bottom: '40px',
                      transform: 'translateX(-50%)',
                      pointerEvents: 'none'
                    }}
                  >
                    <div className="flex items-center gap-2">
                      {getCurrentSectionTitle() && (
                        <p 
                          className="text-[#FD429C] text-[12px] font-bold whitespace-nowrap"
                          style={{ fontFamily: 'Input Sans, monospace' }}
                        >
                          {getCurrentSectionTitle()}
                        </p>
                      )}
                      <p 
                        className="text-white text-[12px] whitespace-nowrap"
                        style={{ fontFamily: 'Input Sans, monospace' }}
                      >
                        {formatTime(getCurrentTime())}
                      </p>
                    </div>
                  </div>
                )}
                {/* Background blur layer */}
                <div 
                  className="absolute w-full bg-[#202020]/20 backdrop-blur-[94px] transition-all duration-200"
                  style={{
                    height: isDragging ? '12px' : '8px',
                    borderRadius: isDragging ? '6px' : '4px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    transition: 'height 0.2s ease-out, border-radius 0.2s ease-out'
                  }}
                />
                {/* White progress bar */}
                <div
                  className="absolute bg-white"
                  style={{ 
                    boxShadow: "0px 0px 2px 0 rgba(0,0,0,0.25)",
                    width: displayProgress === 0 
                      ? (isDragging ? '12px' : '8px')
                      : `${Math.max(displayProgress * 100, isDragging ? 3 : 2)}%`,
                    height: isDragging ? '12px' : '8px',
                    borderRadius: isDragging ? '6px' : '4px',
                    top: '50%',
                    left: 0,
                    transform: 'translateY(-50%)',
                    transition: 'height 0.2s ease-out, border-radius 0.2s ease-out'
                  }}
                />
                
                {/* Section dots */}
                {sections.map((section, index) => {
                  const currentProgress = isDragging ? tempProgress : progress;
                  const isInProgress = section.position <= currentProgress;
                  
                  return (
                    <div
                      key={index}
                      className="absolute w-1 h-1 rounded-full"
                      style={{
                        left: `${section.position * 100}%`,
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: isInProgress ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
                        zIndex: 1
                      }}
                    />
                  );
                })}
              </div>
              <div className="flex justify-between items-center self-stretch flex-grow-0 flex-shrink-0">
                <div className="flex justify-center items-center flex-grow-0 flex-shrink-0 relative gap-2.5">
                  <p className="flex-grow-0 flex-shrink-0 opacity-50 text-[10px] text-left text-white" style={{ fontFamily: 'Input Sans, monospace' }}>
                    {formatTime(getCurrentTime())}
                  </p>
                </div>
                <div className="flex justify-center items-center flex-grow-0 flex-shrink-0 relative gap-2.5">
                  <p className="flex-grow-0 flex-shrink-0 opacity-50 text-[10px] text-left text-white" style={{ fontFamily: 'Input Sans, monospace' }}>
                    {formatTime(totalTime)}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex-grow-0 flex-shrink-0 w-[366px] h-[19px] bg-transparent" />
          
          {/* Enhanced Playback Controls */}
          <div className={`flex justify-center items-center self-stretch flex-grow-0 flex-shrink-0 relative gap-[22px] px-2.5 rounded-[81px] ${
            isImmersiveMode ? 'opacity-0 pointer-events-none' : 'opacity-100'
          }`}
          style={{
            transition: 'opacity 200ms cubic-bezier(0.34, 1.56, 0.64, 1)'
          }}>
            <div className="flex justify-center items-center flex-grow-0 flex-shrink-0 w-11 h-11 relative gap-2.5 p-2.5 rounded-[22px] cursor-pointer transition-all duration-200 active:scale-95" onClick={handleBackward10Seconds}>
              <svg
                width={24}
                height={24}
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="flex-grow-0 flex-shrink-0 w-6 h-6"
                preserveAspectRatio="xMidYMid meet"
              >
                <mask
                  id="mask0_1232_32850"
                  style={{ maskType: "alpha" }}
                  maskUnits="userSpaceOnUse"
                  x={0}
                  y={0}
                  width={24}
                  height={24}
                >
                  <rect width={24} height={24} fill="#D9D9D9" />
                </mask>
                <g mask="url(#mask0_1232_32850)">
                  <path
                    d="M12 22.0005C13.25 22.0005 14.4208 21.763 15.5125 21.288C16.6042 20.813 17.5542 20.1713 18.3625 19.363C19.1708 18.5547 19.8125 17.6047 20.2875 16.513C20.7625 15.4213 21 14.2505 21 13.0005C21 11.7505 20.7625 10.5797 20.2875 9.488C19.8125 8.39633 19.1708 7.44633 18.3625 6.638C17.5542 5.82967 16.6042 5.188 15.5125 4.713C14.4208 4.238 13.25 4.0005 12 4.0005H11.85L12.7 3.1505C12.9 2.9505 12.9958 2.71717 12.9875 2.4505C12.9792 2.18383 12.8833 1.9505 12.7 1.7505C12.5 1.5505 12.2625 1.44633 11.9875 1.438C11.7125 1.42967 11.475 1.5255 11.275 1.7255L8.7 4.3005C8.5 4.5005 8.4 4.73383 8.4 5.0005C8.4 5.26717 8.5 5.5005 8.7 5.7005L11.275 8.2755C11.475 8.4755 11.7125 8.57133 11.9875 8.563C12.2625 8.55467 12.5 8.4505 12.7 8.2505C12.8833 8.0505 12.9792 7.81717 12.9875 7.5505C12.9958 7.28383 12.9 7.0505 12.7 6.8505L11.85 6.0005H12C13.95 6.0005 15.6042 6.67967 16.9625 8.038C18.3208 9.39633 19 11.0505 19 13.0005C19 14.9505 18.3208 16.6047 16.9625 17.963C15.6042 19.3213 13.95 20.0005 12 20.0005C10.05 20.0005 8.39583 19.3213 7.0375 17.963C5.67917 16.6047 5 14.9505 5 13.0005C5 12.7172 4.90417 12.4797 4.7125 12.288C4.52083 12.0963 4.28333 12.0005 4 12.0005C3.71667 12.0005 3.47917 12.0963 3.2875 12.288C3.09583 12.4797 3 12.7172 3 13.0005C3 14.2505 3.2375 15.4213 3.7125 16.513C4.1875 17.6047 4.82917 18.5547 5.6375 19.363C6.44583 20.1713 7.39583 20.813 8.4875 21.288C9.57917 21.763 10.75 22.0005 12 22.0005Z"
                    fill="#FAF7F5"
                  />
                  <path
                    d="M9.75 10C9.96667 10 10.1454 10.0712 10.2871 10.2129C10.4288 10.3546 10.5 10.5333 10.5 10.75V15.25C10.5 15.4667 10.4288 15.6454 10.2871 15.7871C10.1454 15.9288 9.96667 16 9.75 16C9.53333 16 9.35456 15.9288 9.21289 15.7871C9.07122 15.6454 9 15.4667 9 15.25V11.5H8.25C8.03333 11.5 7.85456 11.4288 7.71289 11.2871C7.57122 11.1454 7.5 10.9667 7.5 10.75C7.5 10.5333 7.57122 10.3546 7.71289 10.2129C7.85456 10.0712 8.03333 10 8.25 10H9.75ZM14.5 10C14.7833 10 15.0212 10.0954 15.2129 10.2871C15.4046 10.4788 15.5 10.7167 15.5 11V15C15.5 15.2833 15.4046 15.5212 15.2129 15.7129C15.0212 15.9046 14.7833 16 14.5 16H12.5C12.2167 16 11.9788 15.9046 11.7871 15.7129C11.5954 15.5212 11.5 15.2833 11.5 15V11C11.5 10.7167 11.5954 10.4788 11.7871 10.2871C11.9788 10.0954 12.2167 10 12.5 10H14.5ZM13.5 11.5C13.2239 11.5 13 11.7239 13 12V14C13 14.2761 13.2239 14.5 13.5 14.5C13.7761 14.5 14 14.2761 14 14V12C14 11.7239 13.7761 11.5 13.5 11.5Z"
                    fill="#FAF7F5"
                  />
                </g>
              </svg>
            </div>
            <div className="flex justify-start items-center flex-grow-0 flex-shrink-0 w-11 h-11 relative gap-2.5 p-1.5 rounded-[22px] cursor-pointer transition-all duration-200 active:scale-95" onClick={handleBackToBeginning}>
              <svg
                width={32}
                height={32}
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="flex-grow-0 flex-shrink-0 w-8 h-8 relative"
                preserveAspectRatio="xMidYMid meet"
              >
                <path
                  d="M10.6693 22.6667L10.6693 9.33333C10.6693 8.95556 10.5415 8.63889 10.2859 8.38333C10.0304 8.12778 9.71371 8 9.33594 8C8.95816 8 8.64149 8.12778 8.38594 8.38333C8.13038 8.63889 8.0026 8.95556 8.0026 9.33333L8.0026 22.6667C8.0026 23.0444 8.13038 23.3611 8.38594 23.6167C8.64149 23.8722 8.95816 24 9.33594 24C9.71371 24 10.0304 23.8722 10.2859 23.6167C10.5415 23.3611 10.6693 23.0444 10.6693 22.6667ZM25.3359 21.5L25.3359 10.5C25.3359 10.1 25.2026 9.77778 24.9359 9.53333C24.6693 9.28889 24.3582 9.16667 24.0026 9.16667C23.8915 9.16667 23.7693 9.17778 23.6359 9.2C23.5026 9.22222 23.3804 9.27778 23.2693 9.36667L15.0026 14.9C14.8026 15.0333 14.6526 15.1944 14.5526 15.3833C14.4526 15.5722 14.4026 15.7778 14.4026 16C14.4026 16.2222 14.4526 16.4278 14.5526 16.6167C14.6526 16.8056 14.8026 16.9667 15.0026 17.1L23.2693 22.6333C23.3804 22.7222 23.5026 22.7778 23.6359 22.8C23.7693 22.8222 23.8915 22.8333 24.0026 22.8333C24.3582 22.8333 24.6693 22.7111 24.9359 22.4667C25.2026 22.2222 25.3359 21.9 25.3359 21.5Z"
                  fill="#FAF7F5"
                />
              </svg>
            </div>
            <div 
              className="w-[70px] h-[70px] rounded-full bg-white flex items-center justify-center cursor-pointer transition-all duration-200 active:scale-95"
              onClick={handlePlayPause}
            >
              {localIsPlaying ? (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="6" y="4" width="4" height="16" rx="2" fill="black"/>
                  <rect x="14" y="4" width="4" height="16" rx="2" fill="black"/>
                </svg>
              ) : (
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 28.0567V7.94332C9 7.39271 9.20741 6.93117 9.62222 6.5587C10.037 6.18623 10.521 6 11.0741 6C11.2469 6 11.4284 6.02429 11.6185 6.07287C11.8086 6.12146 11.9901 6.19433 12.163 6.2915L29.0667 16.3482C29.3778 16.5425 29.6111 16.7854 29.7667 17.0769C29.9222 17.3684 30 17.6761 30 18C30 18.3239 29.9222 18.6316 29.7667 18.9231C29.6111 19.2146 29.3778 19.4575 29.0667 19.6518L12.163 29.7085C11.9901 29.8057 11.8086 29.8785 11.6185 29.9271C11.4284 29.9757 11.2469 30 11.0741 30C10.521 30 10.037 29.8138 9.62222 29.4413C9.20741 29.0688 9 28.6073 9 28.0567Z" fill="black"/>
                </svg>
              )}
            </div>
            <div className="flex justify-start items-center flex-grow-0 flex-shrink-0 w-11 h-11 relative gap-2.5 p-1.5 rounded-[22px] cursor-pointer transition-all duration-200 active:scale-95" onClick={onNext}>
              <svg
                width={32}
                height={32}
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="flex-grow-0 flex-shrink-0 w-8 h-8 relative"
                preserveAspectRatio="xMidYMid meet"
              >
                <path
                  d="M21.3307 22.6667V9.33333C21.3307 8.95556 21.4585 8.63889 21.7141 8.38333C21.9696 8.12778 22.2863 8 22.6641 8C23.0418 8 23.3585 8.12778 23.6141 8.38333C23.8696 8.63889 23.9974 8.95556 23.9974 9.33333L23.9974 22.6667C23.9974 23.0444 23.8696 23.3611 23.6141 23.6167C23.3585 23.8722 23.0418 24 22.6641 24C22.2863 24 21.9696 23.8722 21.7141 23.6167C21.4585 23.3611 21.3307 23.0444 21.3307 22.6667ZM6.66406 21.5L6.66406 10.5C6.66406 10.1 6.7974 9.77778 7.06406 9.53333C7.33073 9.28889 7.64184 9.16667 7.9974 9.16667C8.10851 9.16667 8.23073 9.17778 8.36406 9.2C8.4974 9.22222 8.61962 9.27778 8.73073 9.36667L16.9974 14.9C17.1974 15.0333 17.3474 15.1944 17.4474 15.3833C17.5474 15.5722 17.5974 15.7778 17.5974 16C17.5974 16.2222 17.5474 16.4278 17.4474 16.6167C17.3474 16.8056 17.1974 16.9667 16.9974 17.1L8.73073 22.6333C8.61962 22.7222 8.4974 22.7778 8.36406 22.8C8.23073 22.8222 8.10851 22.8333 7.9974 22.8333C7.64184 22.8333 7.33073 22.7111 7.06406 22.4667C6.7974 22.2222 6.66406 21.9 6.66406 21.5Z"
                  fill="#FAF7F5"
                />
              </svg>
            </div>
            <div className="flex justify-center items-center flex-grow-0 flex-shrink-0 w-11 h-11 relative gap-2.5 p-2.5 rounded-[22px] cursor-pointer transition-all duration-200 active:scale-95" onClick={handleForward10Seconds}>
              <svg
                width={24}
                height={24}
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="flex-grow-0 flex-shrink-0 w-6 h-6"
                preserveAspectRatio="xMidYMid meet"
              >
                <mask
                  id="mask0_1232_32840"
                  style={{ maskType: "alpha" }}
                  maskUnits="userSpaceOnUse"
                  x={0}
                  y={0}
                  width={24}
                  height={24}
                >
                  <rect width={24} height={24} fill="#D9D9D9" />
                </mask>
                <g mask="url(#mask0_1232_32840)">
                  <path
                    d="M12 22.0005C10.75 22.0005 9.57917 21.763 8.4875 21.288C7.39583 20.813 6.44583 20.1713 5.6375 19.363C4.82917 18.5547 4.1875 17.6047 3.7125 16.513C3.2375 15.4213 3 14.2505 3 13.0005C3 11.7505 3.2375 10.5797 3.7125 9.488C4.1875 8.39633 4.82917 7.44633 5.6375 6.638C6.44583 5.82967 7.39583 5.188 8.4875 4.713C9.57917 4.238 10.75 4.0005 12 4.0005H12.15L11.3 3.1505C11.1 2.9505 11.0042 2.71717 11.0125 2.4505C11.0208 2.18383 11.1167 1.9505 11.3 1.7505C11.5 1.5505 11.7375 1.44633 12.0125 1.438C12.2875 1.42967 12.525 1.5255 12.725 1.7255L15.3 4.3005C15.5 4.5005 15.6 4.73383 15.6 5.0005C15.6 5.26717 15.5 5.5005 15.3 5.7005L12.725 8.2755C12.525 8.4755 12.2875 8.57133 12.0125 8.563C11.7375 8.55467 11.5 8.4505 11.3 8.2505C11.1167 8.0505 11.0208 7.81717 11.0125 7.5505C11.0042 7.28383 11.1 7.0505 11.3 6.8505L12.15 6.0005H12C10.05 6.0005 8.39583 6.67967 7.0375 8.038C5.67917 9.39633 5 11.0505 5 13.0005C5 14.9505 5.67917 16.6047 7.0375 17.963C8.39583 19.3213 10.05 20.0005 12 20.0005C13.95 20.0005 15.6042 19.3213 16.9625 17.963C18.3208 16.6047 19 14.9505 19 13.0005C19 12.7172 19.0958 12.4797 19.2875 12.288C19.4792 12.0963 19.7167 12.0005 20 12.0005C20.2833 12.0005 20.5208 12.0963 20.7125 12.288C20.9042 12.4797 21 12.7172 21 13.0005C21 14.2505 20.7625 15.4213 20.2875 16.513C19.8125 17.6047 19.1708 18.5547 18.3625 19.363C17.5542 20.1713 16.6042 20.813 15.5125 21.288C14.4208 21.763 13.25 22.0005 12 22.0005ZM9 11.5005H8.25C8.03333 11.5005 7.85417 11.4297 7.7125 11.288C7.57083 11.1463 7.5 10.9672 7.5 10.7505C7.5 10.5338 7.57083 10.3547 7.7125 10.213C7.85417 10.0713 8.03333 10.0005 8.25 10.0005H9.75C9.96667 10.0005 10.1458 10.0713 10.2875 10.213C10.4292 10.3547 10.5 10.5338 10.5 10.7505V15.2505C10.5 15.4672 10.4292 15.6463 10.2875 15.788C10.1458 15.9297 9.96667 16.0005 9.75 16.0005C9.53333 16.0005 9.35417 15.9297 9.2125 15.788C9.07083 15.6463 9 15.4672 9 15.2505V11.5005ZM12.5 16.0005C12.2167 16.0005 11.9792 15.9047 11.7875 15.713C11.5958 15.5213 11.5 15.2838 11.5 15.0005V11.0005C11.5 10.7172 11.5958 10.4797 11.7875 10.288C11.9792 10.0963 12.2167 10.0005 12.5 10.0005H14.5C14.7833 10.0005 15.0208 10.0963 15.2125 10.288C15.4042 10.4797 15.5 10.7172 15.5 11.0005V15.0005C15.5 15.2838 15.4042 15.5213 15.2125 15.713C15.0208 15.9047 14.7833 16.0005 14.5 16.0005H12.5ZM13 14.5005H14V11.5005H13V14.5005Z"
                    fill="#FAF7F5"
                  />
                </g>
              </svg>
            </div>
          </div>
        </div>

        {/* Centered Button Below Play/Pause */}
        <div className={`absolute left-1/2 transform -translate-x-1/2 ${
          isImmersiveMode ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
        style={{
          bottom: '40px', // Moved higher up
          transition: 'opacity 200ms cubic-bezier(0.34, 1.56, 0.64, 1)'
        }}>
          <div className="flex justify-center items-center w-6 h-6 relative gap-[5px] p-2.5 rounded-[56px] bg-[#696969]/20 border-[0.5px] border-white/10 backdrop-blur-[94px] cursor-pointer transition-all duration-200 active:scale-95">
            <svg
              width={18}
              height={18}
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="flex-grow-0 flex-shrink-0 w-[18px] h-[18px]"
              preserveAspectRatio="none"
            >
              <path
                opacity="0.5"
                d="M4 10L8.4855 7.3087C8.80219 7.11869 9.19781 7.11869 9.5145 7.3087L14 10"
                stroke="#FAF7F5"
                strokeWidth={2}
                strokeLinecap="round"
              />
            </svg>
          </div>
        </div>

        </div>
      </div>
    </div>
  );
};

export default MobilePlayer;